/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useCallback } from 'react';
import ReactSelect, { components } from 'react-select';
import AsyncSelect from 'react-select/async';

import UpdatedCtrlHolder from 'pods/forms/components/structural/UpdatedCtrlHolder';
import { executeCallback } from 'core/utils/global-utils';
import { isJssField } from 'core/utils/sitecore-utils';
import { Icon, ICONS } from 'core/components/Icon';
import IProps from 'pods/forms/components/formFieldProps.d';
import * as Typo from 'core/styles/Typography';

import * as S from './Select.style';

const DropdownIndicator = () => <Icon name={ICONS.ARROW_DROP_DOWN} size={24} />;

export type OptionType = {
	label: string;
	value: string;
};

export interface IOptionTypeWithSubLabel extends OptionType {
	subLabel?: string;
}

interface IRawSelectProps {
	width?: number;
	isInline?: boolean;
	hasError?: boolean;
	onChange?: (value: OptionType) => void;
	disabled?: boolean;
	options?: OptionType[];
	loadOptions?: () => void;
	placeholder?: string;
	defaultValue?: string;
	htmlId?: string;
	hasOptionLabel?: boolean;
	optionLabelStartText?: string;
	isLoading?: boolean;
	isMenuPortal?: boolean;
}

function OptionComponent({ ...props }: any) {
	const subLabelText = `${props.selectProps.optionLabelStartText ?? ''} ${props
		.data.subLabel ?? undefined}`;

	return (
		<components.Option {...props}>
			<S.CustomOptionSubLabel>
				<Typo.Body>{props.data.label}</Typo.Body>
				<Typo.BodySmall>{subLabelText}</Typo.BodySmall>
			</S.CustomOptionSubLabel>
		</components.Option>
	);
}

export const RawSelect = ({
	width,
	isInline = false,
	hasError = false,
	onChange,
	disabled = false,
	options,
	placeholder = 'Select...',
	defaultValue,
	htmlId,
	hasOptionLabel,
	optionLabelStartText,
	isLoading,
	isMenuPortal = false,
}: Omit<IRawSelectProps, 'loadOptions'>) => {
	const [selectedOption, setSelectedOption] = useState<OptionType | null>(
		// Set the initial value
		options?.find(item => item.value === defaultValue) ?? null,
	);

	const _handleChange = useCallback(
		(option: OptionType) => {
			setSelectedOption(option);
			executeCallback(onChange, option);
		},
		[onChange],
	);

	useEffect(() => {
		if (!defaultValue) {
			// Reset selected state if the default value change to undefined
			setSelectedOption(null);
		} else {
			const value = options?.find(item => item.value === defaultValue) ?? null;
			if (value) {
				_handleChange(value);
			}
		}
	}, [defaultValue]);

	return (
		<S.SelectContainer {...{ hasError, isInline, width, selectedOption }}>
			<label>
				<ReactSelect
					className={S.SELECT_INPUT_CLASS}
					classNamePrefix={S.SELECT_INPUT_CLASS}
					isSearchable={false}
					value={selectedOption}
					onChange={_handleChange as any}
					isDisabled={disabled}
					components={{
						DropdownIndicator,
						...(hasOptionLabel && {
							Option: function(optionProps) {
								return OptionComponent(optionProps);
							},
						}),
					}}
					inputId={htmlId}
					{...{ placeholder, options }}
					aria-label="Select"
					optionLabelStartText={optionLabelStartText}
					isLoading={isLoading}
					menuPlacement={'auto'}
					menuPortalTarget={isMenuPortal ? document.body : undefined}
				/>
			</label>
		</S.SelectContainer>
	);
};

const SelectAsync = ({
	width,
	isInline = false,
	hasError = false,
	onChange,
	disabled = false,
	loadOptions,
	placeholder = 'Select...',
	defaultValue,
	isMenuPortal = false,
}: Omit<IRawSelectProps, 'options'>) => {
	const [selectedOption, setSelectedOption] = useState<OptionType | null>(
		// Set the initial value
		null,
	);

	const _handleChange = useCallback(
		(option: OptionType) => {
			setSelectedOption(option);
			executeCallback(onChange, option);
		},
		[onChange],
	);

	useEffect(() => {
		if (!defaultValue) {
			// Reset selected state if the default value change to undefined
			setSelectedOption(null);
		}
	}, [defaultValue]);

	return (
		<S.SelectContainer {...{ hasError, isInline, width, selectedOption }}>
			<label>
				<AsyncSelect
					className={S.SELECT_INPUT_CLASS}
					classNamePrefix={S.SELECT_INPUT_CLASS}
					isSearchable={false}
					value={selectedOption}
					onChange={_handleChange as any}
					isDisabled={disabled}
					placeholder={placeholder}
					menuPlacement={'auto'}
					menuPortalTarget={isMenuPortal ? document.body : undefined}
					// Async options
					loadOptions={loadOptions as any}
					defaultOptions
				/>
			</label>
		</S.SelectContainer>
	);
};

const SelectComponent = ({
	config,
	computed,
	htmlId,
	value,
	handleChange,
	getDisplayError,
	renderTextNode,
	ctrlHolderProps,
}: IProps) => {
	const { data = {}, appendMsg, bottomMsg } = config;
	const {
		options,
		loadOptions,
		labelHelpText,
		inputAttrs = {},
		isMenuPortal,
		hasOptionLabel,
		optionLabelStartText,
		handleChangeFromConfig,
	} = data;

	const label = isJssField(data.label)
		? data.label
		: renderTextNode(data.label, 'span');
	const helpText = isJssField(labelHelpText)
		? labelHelpText
		: renderTextNode(labelHelpText, 'span');
	const tooltip = renderTextNode(data.tooltip);
	const errorMsg = getDisplayError();
	const hasError = !!errorMsg;
	const { required, disabled } = computed;
	// use label value if placeholder is not provided
	const placeholder = data.placeholder ?? data.label?.value ?? data.label;

	const _ctrlHolderProps = {
		htmlId,
		label,
		helpText,
		errorMsg,
		required,
		appendMsg,
		value,
		disabled,
		bottomMsg,
		type: inputAttrs?.type,
		fullWidth: data.fullWidth,
		...(data.tooltip && { tooltip }),
		...ctrlHolderProps,
	};

	const Component = data.async ? SelectAsync : RawSelect;
	const selectOptions = data.async ? { loadOptions } : { options };

	return (
		<UpdatedCtrlHolder {..._ctrlHolderProps}>
			<Component
				onChange={(selectedOption: OptionType) => {
					handleChange(selectedOption.value);
					handleChangeFromConfig?.(selectedOption);
				}}
				defaultValue={value}
				{...selectOptions}
				{...{
					disabled,
					hasError,
					placeholder,
					value,
					htmlId,
					isMenuPortal,
					hasOptionLabel,
					optionLabelStartText,
				}}
			/>
		</UpdatedCtrlHolder>
	);
};

export default SelectComponent;
